import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import RecordNotFound from "../../../../../../restaurant/shared/recordNotFound";
import { ORDER_TYPE } from "../utils/constants.ts";
import {
  getAddonRows,
  getBaseMenuItemPrice,
  getPaidQuantityForItem,
  getSpiceLevelName,
  type PaidOrderItem,
} from "../utils/splitCalc.ts";
import { distinctIdentities, getSplitItemKey } from "../hooks/splitItemIdentity.ts";
import { useInlineQuantityEdit } from "../hooks/useInlineQuantityEdit.ts";
import { showPrice, getTranslatedKey, TrimText } from "../../../../../../../utils/commonfunction.js";
import { CONSUMPTION_TAX_RATES, DISPLAY_METHOD_TAX_OPTIONS } from "../../../../../../../utils/Constants.js";
import type { CombinedOrderItem } from "../hooks/useOrderData";
import type { RestSettingDetails } from "../hooks/types.ts";

interface RootState {
  restSettings: {
    data: RestSettingDetails;
  };
}

/** This row is width-constrained; the split picker shows the name in full. */
const SPICE_LEVEL_MAX_CHARS = 10;

interface OrderItemsListProps {
  items: CombinedOrderItem[];
  paidItems?: PaidOrderItem[];
  showPaymentStatus?: boolean;
  updatingKey: string | null;
  onDecreaseQuantity: (item: CombinedOrderItem) => void;
  onIncreaseQuantity: (item: CombinedOrderItem) => void;
  onDeleteItem: (item: CombinedOrderItem) => void;
  onSetQuantity: (item: CombinedOrderItem, quantity: number) => void;
}

const OrderItemsList: React.FC<OrderItemsListProps> = ({
  items,
  paidItems = [],
  showPaymentStatus = false,
  updatingKey,
  onDecreaseQuantity,
  onIncreaseQuantity,
  onDeleteItem,
  onSetQuantity,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const orderType = (location.state?.type ? location.state?.type : searchParams.get("type")) ?? "";
  const restSettingDetails = useSelector((state: RootState) => state?.restSettings?.data);

  const {
    editingKey,
    inputValue,
    inputRef,
    setInputValue,
    startEdit,
    commitEdit,
    handleKeyDown,
  } = useInlineQuantityEdit(onSetQuantity);

  const hasSplitPayment = localStorage.getItem("has_split_payment") === "true";
  const translateValue = getTranslatedKey as (key: string, data?: unknown) => string;
  // Every line's identity, so a legacy paid row lacking ordered_line_id can
  // still be resolved unambiguously instead of pooling paid status onto
  // every line that shares its menu item/price/tax.
  const knownIdentities = useMemo(() => distinctIdentities(items), [items]);

  const getItemLineTotal = useCallback(
    (
      item: { price?: number; total_price?: number; quantity?: number },
      quantity: number = Number(item?.quantity || 0)
    ): number => {
      const unitPrice =
        restSettingDetails?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED
          ? item?.price
          : item?.total_price;
      return Number(unitPrice || 0) * Number(quantity || 0);
    },
    [restSettingDetails?.display_method_product_price]
  );

  const isTaxExcluded =
    restSettingDetails?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED;

  const handleAsterisk = (item: CombinedOrderItem): React.ReactNode => {
    if (
      item?.consumption_tax_rate === CONSUMPTION_TAX_RATES.TAKE_OUT &&
      restSettingDetails?.icon_display_eligible_for_reduced_tax_rate
    ) {
      return <span className="fw-bold">*</span>;
    }
    return null;
  };

  if (!items.length) {
    return <RecordNotFound heading="no_item_found" />;
  }

  return (
    <>
      {items.map((item) => {
        const itemKey = getSplitItemKey(item);
        const addonRows = getAddonRows(item, translateValue, isTaxExcluded);
        const baseMenuItemPrice = getBaseMenuItemPrice(
          item,
          translateValue,
          isTaxExcluded,
        );
        const spiceLevelName = getSpiceLevelName(item, translateValue);
        const isUpdatingItem = updatingKey === itemKey;
        const isEditingQuantity = editingKey === itemKey;
        const isDisabled = hasSplitPayment || isUpdatingItem;
        const totalQuantity = Math.max(0, Math.trunc(Number(item.quantity || 0)));
        const paidQuantity = showPaymentStatus
          ? Math.max(
              0,
              Math.min(totalQuantity, Math.trunc(getPaidQuantityForItem(item, paidItems, knownIdentities))),
            )
          : 0;
        const remainingQuantity = Math.max(totalQuantity - paidQuantity, 0);
        const paymentProgressLabel = t("label_items_paid_count", {
          paid: paidQuantity,
          total: totalQuantity,
        });

        return (
          <div
            className="menu-qty-row cashier-order-row position-relative px-3"
            key={String(itemKey)}
          >
            <div className="cashier-order-item-info">
              <div className="cashier-order-item-title">
                <h4 className="cashier-order-item-name">
                  {translateValue("item_name", item)} {handleAsterisk(item)}
                </h4>
                <strong className="cashier-order-item-base-price">
                  ¥ {showPrice(baseMenuItemPrice)}
                </strong>
              </div>

              {spiceLevelName && orderType !== ORDER_TYPE.EDIT_SALES && (
                <p className="mb-0 fs-12">
                  {t("spice_level_text")} -{" "}
                  <strong className="text-capitalize">
                    {TrimText(spiceLevelName, SPICE_LEVEL_MAX_CHARS)}
                  </strong>
                </p>
              )}
              {/* Plain text, no <span>: `.menu-qty-row span` (style.css:31)
                  renders every span in this row as a bordered 28px pill. */}
              {addonRows.map(
                (row, addonIndex) => (
                  <p
                    className="mb-0 fs-12 text-muted"
                    key={`${itemKey}-addon-${addonIndex}`}
                  >
                    {`+ ${row.label}`}
                    {row.price !== null ? `  ¥ ${showPrice(row.price)}` : ""}
                  </p>
                )
              )}

              {showPaymentStatus ? (
                <div className="cashier-order-item-payment-status-container">
                  <p
                    className={`cashier-order-item-payment-status mb-0 ${
                      remainingQuantity === 0 ? "is-fully-paid" : "is-partially-paid"
                    }`}
                    role="status"
                    aria-live="polite"
                  >
                    {remainingQuantity === 0 ? (
                      <span className="cashier-order-item-payment-status-icon" aria-hidden="true">
                        ✓
                      </span>
                    ) : null}
                    <span>
                      {remainingQuantity === 0
                        ? t("label_item_fully_paid")
                        : paymentProgressLabel}
                    </span>
                  </p>
                </div>
              ) : null}
            </div>

            <div className="cashier-order-row-controls">
              <div className="cashier-order-stepper">
                <button
                  type="button"
                  disabled={isDisabled || Number(item?.quantity ?? 0) <= 1}
                  onClick={() => onDecreaseQuantity(item)}
                >
                  -
                </button>

                {isEditingQuantity ? (
                  <input
                    ref={inputRef}
                    type="number"
                    min={1}
                    value={inputValue}
                    className="cashier-order-qty-input"
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={() => commitEdit(item)}
                    onKeyDown={(e) => handleKeyDown(e, item)}
                  />
                ) : (
                  <span
                    className={!isDisabled ? "cashier-order-qty-editable" : ""}
                    title={!isDisabled ? t("click_to_edit_quantity") : undefined}
                    onClick={() => startEdit(item, itemKey, isDisabled)}
                  >
                    {item?.quantity}
                  </span>
                )}

                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => onIncreaseQuantity(item)}
                >
                  +
                </button>
              </div>

              <h4 className="cashier-order-price">¥ {showPrice(getItemLineTotal(item))}</h4>

              <button
                type="button"
                className="cashier-order-delete"
                disabled={hasSplitPayment}
                onClick={() => onDeleteItem(item)}
              >
                <i className="ti ti-trash"></i>
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default OrderItemsList;
